import { Building2Icon, CalendarDaysIcon, ClipboardListIcon, Loading2, Mail, NetworkIcon, Pencil, SendIcon } from 'assets/icons';
import EmployeeAvatar from 'components/assign/EmployeeAvatar';
import { cardIconButtonClass } from 'components/card/PanelCard';
import CompactEmptyState from 'components/EmptyState/CompactEmptyState';
import IconTooltip from 'components/iconTooltip/IconTooltip';
import useModal from 'components/modal/useModal';
import RecordActivity from 'components/record/activity/RecordActivity';
import { callAction } from 'components/record/callAction';
import { errorCodeOf } from 'components/record/recordErrors';
import { buildActivity } from 'components/record/activity/activityItems';
import { AmbassadorBadge } from 'components/record/ContactRow';
import QuickActions from 'components/record/QuickActions';
import { RecordField } from 'components/record/RecordFields';
import RecordIdentity from 'components/record/RecordIdentity';
import RecordLayout, { RecordSection } from 'components/record/RecordLayout';
import StyledInput from 'components/styledInput/StyledInput';
import { companyRoleLabel } from 'constants/companyRoles';
import { useOnChange } from 'hooks/useChangeSignal';
import UseContext from 'hooks/useContext';
import useCrmContext from 'hooks/useCrmContext';
import useRecordChanged from 'hooks/useRecordChanged';
import useRecordPanels from 'hooks/useRecordPanels';
import useTaskPanels from 'hooks/useTaskPanels';
import useTasksChanged from 'hooks/useTasksChanged';
import useText from 'hooks/useText';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import crmApi from 'server/crm/crmApi';
import { fullName } from './recordItems';

const LEAD_STATUSES = ['', 'ambassadeur'];

/**
 * The page of a contact, on the model of a company's: who they are, how to
 * reach them, their lead status, the companies (and groups) they work for,
 * and the activity that concerns them. A member of a company is a real user
 * of Jobbiz (`/company/:companyId/contacts/:contactId`); a contact of a group
 * comes from the api's group (`/company/group/:groupId/contacts/:contactId`).
 */
const ContactPage = () => {
  const { companyId, groupId, contactId } = useParams();
  const navigate = useNavigate();
  const { text, language } = useText('record');
  const { text: activityText } = useText('recordActivity');
  const { call, ready } = useCrmContext();
  const { setModalData } = UseContext();
  const { toggle } = useModal();
  const { openRecordForm } = useRecordPanels();
  const { openTask, openTaskForm } = useTaskPanels();
  const [record, setRecord] = useState(null);
  const [failed, setFailed] = useState(null);

  const load = useCallback(
    () =>
      call(crmApi.getContactRecord, { companyId, groupId, contactId }).then(
        (next) => {
          setRecord(next);
          setFailed(null);
        },
        // A refusal of the api is worded here, never with its own message.
        (error) => {
          const code = errorCodeOf(error);
          if (code === 'GROUP_NOT_FOUND') setFailed(text('groupMissing'));
          else if (code || error?.response) setFailed(text('groupLoadFailedText'));
          else setFailed(error.message);
        },
      ),
    [call, companyId, groupId, contactId, text],
  );
  useEffect(() => {
    if (ready) load();
  }, [load, ready]);
  useRecordChanged(load);
  useTasksChanged(load);
  useOnChange('employee_created', load);

  const team = useMemo(() => record?.team ?? [], [record]);
  const nameOf = useCallback(
    (personId) => {
      const person = team.find((member) => member.id === personId);
      return person ? fullName(person) : text('someone');
    },
    [team, text],
  );
  const name = record ? fullName(record.person) : '';
  const items = useMemo(
    () => (record ? buildActivity(record.history, { now: new Date(), text: activityText, nameOf, contact: name }) : []),
    [record, activityText, nameOf, name],
  );

  if (failed || !record) {
    return (
      <RecordLayout side={null}>
        {failed ? (
          <CompactEmptyState
            glyph="searchX"
            title={text('contactMissing')}
            description={failed}
            action={{ label: text('backCompanies'), onClick: () => navigate('/company') }}
          />
        ) : (
          <div className="flex justify-center p-8" aria-busy="true">
            <span className="animate-spin">
              <Loading2 wh={24} color="#FF9600" />
            </span>
          </div>
        )}
      </RecordLayout>
    );
  }

  const { person, owner, memberships } = record;
  const ownerPath = (place) => (place.kind === 'group' ? `/company/group/${place.id}` : `/company/${place.id}`);
  const target = owner.kind === 'group' ? { groupId: owner.id } : { companyId: owner.id };
  const me = { id: person.id, name, email: person.email, phone: person.phone };
  // A note is linked to the contact itself: its account and its company.
  const noteContact = { id: person.id, name, companyId: owner.kind === 'company' ? owner.id : null };
  const openForm = (kind, extra = {}) =>
    openRecordForm(kind, {
      ...target,
      recordName: owner.name,
      contacts: [me],
      contactName: name,
      ...(kind === 'note' && { contact: noteContact }),
      ...extra,
    });
  const add = (kind) => {
    if (kind === 'task') openTaskForm({ defaults: { type: 'todo', ...target, contactName: name, scoped: true } });
    else openForm(kind === 'all' ? 'note' : kind);
  };
  const openItem = (ref) => {
    if (ref.kind === 'task') openTask(ref.id);
    if (ref.kind === 'client') navigate(`/company/${ref.companyId ?? owner.id}?onglet=${ref.tab}`);
  };
  const setStatus = (status) =>
    call(crmApi.setLeadStatus, { personId: person.id, status }).then(
      () => {
        toast.success(status ? text('ambassadorSet', { name }) : text('ambassadorUnset'));
        load();
      },
      (error) => toast.error(error.message),
    );
  const invite = () => {
    setModalData({
      idCompany: owner.id,
      origin: 'hubspot',
      hubspotContactId: person.hubspotId,
      prefill: { email: person.email, firstName: person.firstname, lastName: person.lastname, phone: person.phone },
    });
    toggle('add_company_member');
  };
  const groups = memberships.filter((place) => place.kind === 'group').length;
  const placeDetail = (place) => {
    if (place.kind === 'group') return [place.position || text('noPosition'), text('groupContact')].join(' · ');
    const access =
      place.account === 'active' && place.role
        ? text('access', { role: companyRoleLabel(language, place.role) })
        : place.account === 'pending'
          ? text('pending')
          : place.id === owner.id && !place.account
            ? text('notOnJobbiz')
            : '';
    return [place.position || text('noPosition'), access].filter(Boolean).join(' · ');
  };

  return (
    <RecordLayout
      side={
        <>
          <RecordIdentity
            back={{ label: owner.name, onClick: () => navigate(ownerPath(owner)) }}
            picture={<EmployeeAvatar employee={person} size={48} />}
            title={name}
            subtitle={
              memberships.length > 1
                ? text('workingFor', { count: memberships.length })
                : [person.position, owner.name].filter(Boolean).join(' · ')
            }
          >
            {person.leadStatus === 'ambassadeur' && (
              <div className="mt-2">
                <AmbassadorBadge />
              </div>
            )}
            <QuickActions
              actions={[
                { key: 'note', label: text('actionNote'), icon: Pencil, onClick: () => openForm('note') },
                { key: 'email', label: text('actionEmail'), icon: Mail, onClick: () => openForm('email') },
                callAction({ label: text('actionCall'), people: [me], onNone: () => toast.info(text('noPhone')) }),
                { key: 'task', label: text('actionTask'), icon: ClipboardListIcon, onClick: () => add('task') },
                { key: 'event', label: text('actionMeeting'), icon: CalendarDaysIcon, onClick: () => openForm('event') },
              ]}
            />
          </RecordIdentity>
          <RecordSection
            title={text('information')}
            action={
              owner.kind === 'group' &&
              record.access?.canEditContacts && (
                <IconTooltip label={text('editContact', { name })}>
                  <button
                    type="button"
                    aria-label={text('editContact', { name })}
                    onClick={() => openForm('groupContact', { contact: person, afterDelete: ownerPath(owner) })}
                    className={cardIconButtonClass}
                  >
                    <Pencil wh={18} color="#374151" />
                  </button>
                </IconTooltip>
              )
            }
          >
            <RecordField label={text('fieldEmail')}>
              {person.email && (
                <a href={`mailto:${person.email}`} className="hover:underline">
                  {person.email}
                </a>
              )}
            </RecordField>
            <RecordField label={text('fieldPhone')}>{person.phone}</RecordField>
            <RecordField label={text('fieldLead')}>
              <div className="mt-1.5">
                <StyledInput
                  type="select"
                  value={person.leadStatus ?? ''}
                  list={LEAD_STATUSES.map((status) => ({ value: status, label: text(`lead_${status || 'none'}`) }))}
                  onChange={(event) => setStatus(event.target.value)}
                />
              </div>
            </RecordField>
          </RecordSection>
          <RecordSection title={text(groups ? 'placesWithGroups' : 'places', { count: memberships.length })}>
            {memberships.map((place) => (
              <div key={`${place.kind}-${place.id}`} className="flex items-center gap-3 border-b border-gray-100 py-2.5 last:border-0">
                <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-gray-100 text-gray-900">
                  {place.kind === 'group' ? <NetworkIcon wh={16} color="currentColor" /> : <Building2Icon wh={16} color="currentColor" />}
                </span>
                <button type="button" onClick={() => navigate(ownerPath(place))} className="min-w-0 flex-1 text-left">
                  <span className="block truncate text-sm font-medium text-gray-900 hover:underline">{place.name}</span>
                  <span className="block truncate text-xs text-gray-600">{placeDetail(place)}</span>
                </button>
                {place.id === owner.id && owner.kind === 'company' && person.hubspotId && (
                  <IconTooltip label={text('inviteAt', { company: owner.name })}>
                    <button type="button" aria-label={text('inviteAt', { company: owner.name })} onClick={invite} className={cardIconButtonClass}>
                      <SendIcon wh={18} color="#374151" />
                    </button>
                  </IconTooltip>
                )}
              </div>
            ))}
          </RecordSection>
        </>
      }
    >
      <p className="text-sm font-medium text-gray-900">{text('tab_activity')}</p>
      <RecordActivity items={items} onAdd={add} onOpen={openItem} />
    </RecordLayout>
  );
};

export default ContactPage;
